import { ProjectProps } from "./projectDetails";
import Link from "next/link";
import Image from "next/image";
import AnimatedTitle from "../shared/animations/AnimatedTitle";
import AnimatedBody from "../shared/animations/AnimatedBody";
import { motion } from "motion/react";
import Container from "../shared/container/Container";
import React from "react";
import { SiGithub } from "react-icons/si";
import { BsLink45Deg } from "react-icons/bs";
import ImagePlay from "../video-player/ImagePlay";
import posthog from "posthog-js";
import { useToast } from "@/hooks/use-toast";
const ProjectCard = ({
  id,
  name,
  description,
  technologies,
  techNames,
  techLinks,
  github,
  demo,
  image,
  video,
  available,
}: ProjectProps) => {
  const { toast } = useToast();

  return (
    <motion.div
      className={`relative z-10 h-[550px] w-full items-stretch justify-center bg-cover bg-center bg-no-repeat py-0`}
      initial="initial"
      animate="animate"
    >
      <Container
        width="100%"
        height="100%"
        borderRadius={25}
        color="rgba(255, 255, 255, 0.1)"
        blur={false}
        grain={true}
        top="0px"
        left="0px"
        angle={0}
      >
        {video === "" ? (
          <Image
            src={image}
            alt={name}
            width={500}
            height={500}
            className={`absolute -bottom-2 w-[70%] sm:w-[85%] md:w-[60%] lg:max-w-[55%] ${
              id % 2 === 0 ? "right-0" : "left-0"
            }`}
            priority={true}
            onClick={() => {
              triggerToast(toast);
              posthog.capture("project_video", {
                project: name,
                type: "fail_ImageOnly",
              });
            }}
          />
        ) : (
          <ImagePlay
            name={name}
            image={image}
            video={video || ""}
            width={500}
            height={500}
            className={`absolute -bottom-2 w-[70%] sm:w-[85%] md:w-[60%] lg:max-w-[55%] ${
              id % 2 === 0 ? "right-0" : "left-0"
            }`}
          />
        )}

        <div
          className={`absolute top-0 text-[#0E1016] ${
            id % 2 === 0 ? "left-0 ml-8 lg:ml-14" : "right-0 mr-8 lg:mr-14"
          } mt-6 flex  items-center justify-center gap-4 lg:mt-10`}
        >
          {available ? (
            <>
              <ProjectLink
                href={github}
                label="Open GitHub Repository"
                icon={SiGithub}
                project={name}
                type="repo"
              />
              <ProjectLink
                href={demo}
                label="Open Live Demo"
                icon={BsLink45Deg}
                project={name}
                type="demo"
              />
            </>
          ) : (
            <div></div>
          )}
        </div>
        <div
          className={`absolute text-white  ${
            !(id % 2 === 0)
              ? "right-0 top-32 ml-10 mr-0 md:right-0 md:ml-0 lg:right-0 lg:top-60  lg:mr-4"
              : "left-10 top-32 ml-0 md:mr-12 lg:top-52 lg:ml-4"
          } mb-10  md:mb-16 lg:mb-14 `}
        >
          <AnimatedTitle
            text={name}
            className={
              "max-w-[90%] text-[40px] leading-none text-white md:text-[44px] md:leading-none lg:max-w-[450px] lg:text-[48px] lg:leading-none"
            }
            wordSpace={"mr-[0.25em]"}
            charSpace={"-mr-[0.01em]"}
          />
          <AnimatedBody
            text={description}
            className={
              "mt-4 w-[90%] max-w-[457px] text-[16px] font-semibold text-[#95979D] "
            }
          />
          <div className="col-start-1 col-end-2 mb-9 mt-9 grid grid-cols-5 gap-5">
            {technologies.map((IconComponent, id) => (
              <div key={id} className={"relative"}>
                <Link
                  href={techLinks[id]}
                  target="_blank"
                  aria-label={`Learn more about ${techNames[id]}`}
                  className="w-[20px] text-[20px] md:w-[25px] md:text-[24px] lg:w-[30px] lg:text-[28px]"
                  title={techLinks[id]}
                  data-blobity-tooltip={techNames[id]}
                  data-blobity-magnetic="false"
                  onClick={() => {
                    posthog.capture("project_link", {
                      project: name,
                      label: techNames[id],
                      href: techLinks[id],
                      type: "tech",
                    });
                  }}
                >
                  <IconComponent />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </motion.div>
  );
};

const ProjectLink = ({
  href,
  label,
  icon: Icon,
  project,
  type,
}: {
  href: string;
  label: string;
  icon: React.ElementType;
  project?: string;
  type?: string;
}) => {
  return (
    <Link
      href={href}
      target="_blank"
      aria-label={label}
      className="aspect-square h-[43px] w-[43px] rounded-full bg-white p-3 text-[20px] md:h-[65px] md:w-[65px] md:p-5 md:text-[24px] lg:h-[65px] lg:w-[65px] lg:text-[28px]"
      data-blobity
      data-blobity-radius="35"
      data-blobity-offset-x="4"
      data-blobity-offset-y="4"
      data-blobity-magnetic="false"
      onClick={() => {
        posthog.capture("project_link", {
          project: project,
          label: label,
          href: href,
          type: type || "circle",
        });
      }}
    >
      <Icon />
    </Link>
  );
};

const triggerToast = (toast: any) => {
  // inform user that video is not available, but other can have it
  toast({
    title: "Video is coming soon!",
    description:
      "Currently, this project does not have a video. But other projects do!",
  });
};

export default ProjectCard;
